import moment from 'moment'

const parseVisitDate = (date) => {
  if (date == null || date === '') return moment.invalid()

  const normalizedDate =
    typeof date === 'string'
      ? date.replace(/\.(\d{1,2})(?=\+|Z|$)/, (_, fraction) => {
          return `.${fraction.padEnd(3, '0')}`
        })
      : date

  const parsedDate = moment(normalizedDate, moment.ISO_8601, true)
  return parsedDate.isValid() ? parsedDate : moment.invalid()
}

const formatVisit = ({
  ssvp_id,
  participant_visit_metadata,
  actual_visit_datetime
}) => ({
  visit_id: ssvp_id,
  name:
    participant_visit_metadata?.display_name ||
    participant_visit_metadata?.visit_name,
  visit_datetime: actual_visit_datetime
})

export const flattenParticipantVisits = (ssVisits, adhocVisits = []) => {
  const plannedVisits = Array.isArray(ssVisits)
    ? ssVisits
    : Object.values(ssVisits ?? {}).flatMap((visits) =>
        Object.values(visits ?? {})
      )

  return [
    ...plannedVisits.filter(({ is_adhoc }) => !is_adhoc),
    ...adhocVisits
  ]
}

export const isVisitConfirmed = (visit) => {
  const status =
    visit?.status || visit?.visit_status || visit?.actual_visit_status

  return (
    typeof status === 'string' &&
    status.toLowerCase() === 'confirmed' &&
    parseVisitDate(visit?.actual_visit_datetime).isValid()
  )
}

const collectIdentityKeys = (values) =>
  values
    .filter((value) => value != null && value !== '')
    .map(String)

export const collectVisitIdentityKeys = (visit) => {
  const metadata = visit?.participant_visit_metadata || {}

  return collectIdentityKeys([
    visit?.ssvp_id,
    visit?.id,
    visit?.visit_id,
    visit?.visit_schedule_id,
    visit?.ss_visit_id,
    metadata.visit_id,
    metadata.visit_schedule_id,
    metadata.ssvp_id,
  ])
}

export const collectScheduleVisitKeys = (schedule) =>
  collectIdentityKeys([
    schedule?.chain_deployment_info?.visit_id,
    schedule?.chain_deployment_info?.visit_schedule_id,
  ])

export const getConfirmedVisitKeySet = (visits = []) => {
  const keys = new Set()

  visits.forEach((visit) => {
    if (!isVisitConfirmed(visit)) return
    collectVisitIdentityKeys(visit).forEach((key) => keys.add(key))
  })

  return keys
}

export const isScheduleVisitConfirmed = (schedule, confirmedVisitKeys) => {
  const keys =
    confirmedVisitKeys instanceof Set
      ? confirmedVisitKeys
      : new Set(confirmedVisitKeys || [])

  if (!keys.size) return false

  return collectScheduleVisitKeys(schedule).some((key) => keys.has(key))
}

export const selectRelevantVisits = (
  ssVisits,
  adhocVisits = [],
  now = moment()
) => {
  const visits = flattenParticipantVisits(ssVisits, adhocVisits)

  const confirmedVisits = visits
    .filter(isVisitConfirmed)
    .map(formatVisit)
    .sort(
      (firstVisit, secondVisit) =>
        parseVisitDate(firstVisit.visit_datetime).valueOf() -
        parseVisitDate(secondVisit.visit_datetime).valueOf()
    )

  const currentDateTime = moment.isMoment(now) ? now : parseVisitDate(now)
  const pastVisits = confirmedVisits.filter(({ visit_datetime }) =>
    parseVisitDate(visit_datetime).isBefore(currentDateTime)
  )
  const firstUpcomingVisit = confirmedVisits.find(({ visit_datetime }) =>
    parseVisitDate(visit_datetime).isSameOrAfter(currentDateTime)
  )

  return {
    pastVisits,
    upcomingVisits: firstUpcomingVisit ? [firstUpcomingVisit] : []
  }
}
